import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ScrollView, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { FontFamily } from '../../../../../constants/fonts';
import GroupedColumnPickerPanel, { ColumnPickerGroup, useCloseOnOutsideClick } from './GroupedColumnPickerPanel';
import { getCategoryTagStyle } from './categoryTagStyles';
import { useTheme } from '../../../../../theme/useTheme';
import TablePager from './TablePager';
import { CrewingHorizontalScrollView, CrewingVerticalScrollView } from './webScrollStyles';
import type { HoursCategoryTotals } from '../crewingAnalyticsApi';

export interface HoursByCategoryRow {
  region: string;
  market: string;
  location: string;
  year: string;
  period: string;
  week: string;
  category: string;
  zone: string;
  scheduleHours: number;
  actualHours: number;
  forecastedHours: number;
  estimatedHours: number;
  size: string;
  actualCases: number;
  routes: number;
  siteSqft: number;
}

interface HoursByCategoryTableProps {
  title: string;
  note?: string;
  rows: HoursByCategoryRow[];
  selectedColumnKeys?: ColumnKey[];
  /** Called with the newly-selected dimension keys so the caller can re-fetch server-side grouped rows. */
  onColumnsChange?: (dimensionKeys: string[]) => void;
  /** Aggregate metrics across the FULL filtered result set (backend `totals`) — render this
   * directly for the grand-total row, never recompute it from the current page's `rows`. */
  backendTotals?: HoursCategoryTotals | null;
  /** Full filtered grouped-row count (backend `total`), used by the pager and footnote. */
  total?: number;
  page?: number;
  pageSize?: number;
  onPageChange?: (page: number) => void;
  pageLoading?: boolean;
  /** Selectable rows-per-page values — the picker is hidden if this or `onPageSizeChange` is omitted. */
  pageSizeOptions?: number[];
  onPageSizeChange?: (pageSize: number) => void;
  /** Server-side sort state (backend `sortBy`/`sortDirection`) — rows arrive pre-sorted, so this
   * table no longer sorts client-side. `sortBy` is null/undefined for the default grouping order. */
  sortBy?: string | null;
  sortDirection?: 'asc' | 'desc';
  /** Called with the clicked column's key — caller re-fetches page 0 with the new sort applied. */
  onSortChange?: (columnKey: string) => void;
}

const WIDE = 116;
const STD = 64;
const MED = 100;
const NUM = 110;

type ColumnKey = keyof HoursByCategoryRow;

interface ColumnDef {
  key: ColumnKey;
  label: string;
  width: 'wide' | 'std' | 'med' | 'num';
  numeric?: boolean;
  link?: boolean;
}

const COLUMN_DEFS: ColumnDef[] = [
  { key: 'region', label: 'Region / Unit', width: 'wide' },
  { key: 'market', label: 'Market / POD', width: 'wide' },
  { key: 'location', label: 'Location', width: 'wide', link: true },
  { key: 'year', label: 'Year', width: 'std' },
  { key: 'period', label: 'Period', width: 'std' },
  { key: 'week', label: 'Week', width: 'std' },
  { key: 'category', label: 'Category', width: 'med' },
  { key: 'zone', label: 'Zone', width: 'med' },
  { key: 'scheduleHours', label: 'Schedule Hours', width: 'num', numeric: true },
  { key: 'actualHours', label: 'Actual Hours', width: 'num', numeric: true },
  { key: 'forecastedHours', label: 'Forecasted Hours', width: 'num', numeric: true },
  { key: 'estimatedHours', label: 'Estimated Hours', width: 'num', numeric: true },
  { key: 'size', label: 'Size', width: 'std' },
  { key: 'actualCases', label: 'Actual Cases', width: 'num', numeric: true },
  { key: 'routes', label: 'Routes', width: 'num', numeric: true },
  { key: 'siteSqft', label: 'Site SQFT', width: 'num', numeric: true },
];

// Only these (groupable) columns are toggleable in the picker — metrics always display.
const DIMENSION_KEYS: ColumnKey[] = ['region', 'market', 'location', 'year', 'period', 'week', 'category', 'zone'];
// Location attributes: only meaningful (and displayed) when Location is a selected grouping column.
const LOCATION_ATTRIBUTE_KEYS: ColumnKey[] = ['size', 'actualCases', 'routes', 'siteSqft'];
const METRIC_DEFS = COLUMN_DEFS.filter((col) => !DIMENSION_KEYS.includes(col.key) && !LOCATION_ATTRIBUTE_KEYS.includes(col.key));
const LOCATION_ATTRIBUTE_DEFS = COLUMN_DEFS.filter((col) => LOCATION_ATTRIBUTE_KEYS.includes(col.key));

const COLUMN_GROUPS: ColumnPickerGroup[] = [
  {
    key: 'location-hierarchy',
    label: 'Location Hierarchy',
    options: [
      { key: 'region', label: 'Region / Unit' },
      { key: 'market', label: 'Market / POD' },
      { key: 'location', label: 'Location' },
    ],
  },
  {
    key: 'timeframe',
    label: 'Timeframe',
    options: [
      { key: 'year', label: 'Year' },
      { key: 'period', label: 'Period' },
      { key: 'week', label: 'Week' },
    ],
  },
  {
    key: 'other-attributes',
    label: 'Other Attributes',
    options: [
      { key: 'category', label: 'Category' },
      { key: 'zone', label: 'Zone' },

    ],
  },
];

const num = (value: number) => Math.round(value).toLocaleString();

// Allowed server-side sortBy keys per the backend contract. 'size'/'actualCases'/'routes'/'siteSqft'
// are only sortable (and only rendered) when 'location' is part of the active grouping columns.
const BASE_SORTABLE_KEYS: ColumnKey[] = [...DIMENSION_KEYS, 'scheduleHours', 'actualHours', 'forecastedHours', 'estimatedHours'];
const LOCATION_ONLY_SORTABLE_KEYS: ColumnKey[] = ['size', 'actualCases', 'routes', 'siteSqft'];

const HoursByCategoryTable: React.FC<HoursByCategoryTableProps> = ({ title, note, rows, selectedColumnKeys: controlledSelectedColumnKeys, onColumnsChange, backendTotals, total, page = 0, pageSize = 25, onPageChange, pageLoading, pageSizeOptions, onPageSizeChange, sortBy, sortDirection = 'asc', onSortChange }) => {
  const isDark = useTheme().resolvedTheme === 'dark';

  const [isColumnPickerOpen, setIsColumnPickerOpen] = useState(false);
  const columnPickerWrapRef = useRef<View>(null);
  useCloseOnOutsideClick(columnPickerWrapRef, () => setIsColumnPickerOpen(false), isColumnPickerOpen);
  const [selectedColumnKeys, setSelectedColumnKeys] = useState<ColumnKey[]>(controlledSelectedColumnKeys ?? DIMENSION_KEYS);

  useEffect(() => {
    if (controlledSelectedColumnKeys) {
      setSelectedColumnKeys(controlledSelectedColumnKeys);
    }
  }, [controlledSelectedColumnKeys]);

  const handleColumnsChange = (keys: ColumnKey[]) => {
    setSelectedColumnKeys(keys);
    onColumnsChange?.(keys);
  };

  const selectedColumns = useMemo(
    () => [
      ...COLUMN_DEFS.filter((col) => DIMENSION_KEYS.includes(col.key) && selectedColumnKeys.includes(col.key)),
      ...(selectedColumnKeys.includes('location') ? LOCATION_ATTRIBUTE_DEFS : []),
      ...METRIC_DEFS,
    ],
    [selectedColumnKeys],
  );

  const hasLocationGrouping = selectedColumnKeys.includes('location');

  const isSortable = (key: ColumnKey) =>
    (BASE_SORTABLE_KEYS.includes(key) || (hasLocationGrouping && LOCATION_ONLY_SORTABLE_KEYS.includes(key))) && !!onSortChange;

  const tableMinWidth = useMemo(
    () =>
      selectedColumns.reduce(
        (sum, col) => sum + (col.width === 'wide' ? WIDE : col.width === 'num' ? NUM : col.width === 'med' ? MED : STD),
        0,
      ),
    [selectedColumns],
  );

  const getCellWidthStyle = (width: ColumnDef['width']) => {
    if (width === 'wide') return styles.wideCell;
    if (width === 'num') return styles.numCell;
    if (width === 'med') return styles.medCell;
    return null;
  };

  return (
    <View style={styles.card}>
      <View style={styles.headerContainer}>
        <View style={styles.headerRow}>
          <Text style={styles.title}>{title}</Text>
          <View style={styles.columnPickerWrap} ref={columnPickerWrapRef}>
            <TouchableOpacity
              style={styles.columnsPill}
              onPress={() => setIsColumnPickerOpen((prev) => !prev)}
            >
              <Text style={styles.columnsPillText}>{`Columns (${selectedColumnKeys.length})`}</Text>
            </TouchableOpacity>

            {isColumnPickerOpen && (
              <GroupedColumnPickerPanel
                groups={COLUMN_GROUPS}
                selectedKeys={selectedColumnKeys}
                onChange={(keys) => handleColumnsChange(keys as ColumnKey[])}
              />
            )}
          </View>
        </View>
        {note ? <Text style={styles.note}>{note}</Text> : null}
      </View>

      <CrewingHorizontalScrollView horizontal showsHorizontalScrollIndicator contentContainerStyle={styles.scrollContent}>
        <View style={[styles.tableInner, { minWidth: tableMinWidth }]}>
          <View style={styles.tableHead}>
            {selectedColumns.map((column) => {
              const isActive = sortBy === column.key;
              const sortable = isSortable(column.key);
              return (
                <TouchableOpacity
                  key={column.key}
                  style={[styles.headCell, getCellWidthStyle(column.width), column.numeric && styles.headCellNumeric]}
                  onPress={sortable ? () => onSortChange!(column.key) : undefined}
                  disabled={!sortable}
                  activeOpacity={sortable ? 0.7 : 1}
                >
                  <Text style={[styles.headCellText, isActive && styles.headCellTextActive, column.numeric && styles.headCellTextNumeric]} numberOfLines={2}>
                    {column.label}
                  </Text>
                  {sortable && (
                    <MaterialIcons
                      name={isActive ? (sortDirection === 'asc' ? 'arrow-drop-up' : 'arrow-drop-down') : 'unfold-more'}
                      size={14}
                      color={isActive ? '#034895' : '#9CA3AF'}
                    />
                  )}
                </TouchableOpacity>
              );
            })}
          </View>

          <View style={styles.totalRow}>
            {selectedColumns.map((column, index) => {
              let value = '--';
              if (column.key === 'region') value = 'Total';
              if (column.numeric) {
                const raw = backendTotals ? backendTotals[column.key as keyof HoursCategoryTotals] : null;
                value = raw == null ? '--' : num(Number(raw));
              }

              return (
                <Text key={`total-${column.key}-${index}`} style={[styles.totalCell, getCellWidthStyle(column.width)]}>
                  {value}
                </Text>
              );
            })}
          </View>

          <View style={styles.tableViewport}>
            <CrewingVerticalScrollView nestedScrollEnabled showsVerticalScrollIndicator>
              {rows.map((row, index) => (
                <View key={`${row.location}-${row.week}-${index}`} style={styles.tableRow}>
                  {selectedColumns.map((column) => {
                    const rawValue = row[column.key];
                    const isPlaceholderText = (column.key === 'region' || column.key === 'market') && (rawValue === '0' || rawValue === 0 || !rawValue);
                    const displayValue = column.numeric
                      ? num(Number(rawValue ?? 0))
                      : isPlaceholderText
                        ? '--'
                        : String(rawValue ?? '--');

                    if (column.key === 'category' && displayValue !== '--') {
                      const tagStyle = getCategoryTagStyle(displayValue, isDark);
                      return (
                        <View key={`${row.location}-${index}-${column.key}`} style={[getCellWidthStyle(column.width), styles.categoryCell]}>
                          <View style={[styles.categoryTag, { backgroundColor: tagStyle.backgroundColor }]}>
                            <Text style={[styles.categoryTagText, { color: tagStyle.color }]} numberOfLines={1}>
                              {displayValue}
                            </Text>
                          </View>
                        </View>
                      );
                    }

                    return (
                      <Text
                        key={`${row.location}-${index}-${column.key}`}
                        style={[
                          styles.bodyCell,
                          getCellWidthStyle(column.width),
                          column.link ? styles.linkCell : null,
                        ]}
                        numberOfLines={1}
                        ellipsizeMode="tail"
                      >
                        {displayValue}
                      </Text>
                    );
                  })}
                </View>
              ))}
            </CrewingVerticalScrollView>
          </View>
        </View>
      </CrewingHorizontalScrollView>

      <Text style={styles.footerNote}>{`${(total ?? rows.length).toLocaleString()} groups total. Hours and cases sum.`}</Text>
      {onPageChange && (
        <TablePager page={page} pageSize={pageSize} total={total ?? rows.length} onPageChange={onPageChange} loading={pageLoading} pageSizeOptions={pageSizeOptions} onPageSizeChange={onPageSizeChange} />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  card: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    padding: 14,
    overflow: 'visible',
  },
  headerContainer: {
    flexDirection: 'column',
    gap: 6,
    marginBottom: 8,
    position: 'relative',
    zIndex: 30,
  },
  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 10,
    position: 'relative',
    zIndex: 30,
  },
  title: {
    fontSize: 16,
    fontFamily: FontFamily.bold,
    color: '#1A1D21',
    fontWeight: '700',
  },
  headerMetaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  note: {
    fontSize: 10,
    fontFamily: FontFamily.regular,
    color: '#838383',
  },
  columnsPill: {
    backgroundColor: '#EFF6FF',
    borderWidth: 1,
    borderColor: '#BFDBFE',
    borderRadius: 8,
    paddingHorizontal: 8,
    paddingVertical: 3,
  },
  columnsPillText: {
    fontSize: 11,
    color: '#0B5FC6',
    fontWeight: '600',
  },
  columnPickerWrap: {
    position: 'relative',
    zIndex: 40,
  },
  scrollContent: {
    flexGrow: 1,
  },
  tableInner: {
    width: '100%',
  },
  tableViewport: {
    flex: 1,
    minHeight: 260,
    maxHeight: 420,
  },
  tableHead: {
    flexDirection: 'row',
    alignItems: 'stretch',
    borderBottomWidth: 2,
    borderBottomColor: '#D7E5F8',
    backgroundColor: '#FFFFFF',
    paddingVertical: 8,
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#D7E5F8',
    paddingVertical: 8,
  },
  totalRow: {
    flexDirection: 'row',
    borderTopWidth: 1,
    borderTopColor: '#D0D0D0',
    borderBottomWidth: 2,
    borderBottomColor: '#D7E5F8',
    backgroundColor: '#F3F7FD',
    paddingVertical: 9,
  },
  headCell: {
    flexGrow: 1,
    flexBasis: STD,
    minWidth: STD,
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 2,
    paddingHorizontal: 6,
  },
  headCellText: {
    flex: 1,
    fontSize: 10,
    fontFamily: FontFamily.bold,
    color: '#616161',
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: 0.3,
  },
  headCellTextActive: {
    color: '#034895',
  },
  headCellNumeric: {
    justifyContent: 'flex-end',
  },
  headCellTextNumeric: {
    textAlign: 'right',
  },
  bodyCell: {
    flexGrow: 1,
    flexBasis: STD,
    minWidth: STD,
    paddingHorizontal: 6,
    fontSize: 12,
    fontFamily: FontFamily.medium,
    color: '#3A3A3A',
  },
  totalCell: {
    flexGrow: 1,
    flexBasis: STD,
    minWidth: STD,
    paddingHorizontal: 6,
    fontSize: 12,
    fontFamily: FontFamily.bold,
    color: '#2D2D2D',
    fontWeight: '700',
  },
  wideCell: {
    flexGrow: 1,
    flexBasis: WIDE,
    minWidth: WIDE,
  },
  medCell: {
    flexGrow: 1,
    flexBasis: MED,
    minWidth: MED,
  },
  numCell: {
    flexGrow: 1,
    flexBasis: NUM,
    minWidth: NUM,
    textAlign: 'right',
  },
  linkCell: {
    color: '#0B5FC6',
    fontWeight: '600',
  },
  categoryCell: {
    paddingHorizontal: 6,
    justifyContent: 'center',
  },
  categoryTag: {
    alignSelf: 'flex-start',
    borderRadius: 12,
    paddingHorizontal: 8,
    paddingVertical: 2,
  },
  categoryTagText: {
    fontSize: 11,
    fontFamily: FontFamily.medium,
    fontWeight: '600',
  },
  footerNote: {
    marginTop: 10,
    fontSize: 12,
    fontFamily: FontFamily.regular,
    color: '#6B7280',
    lineHeight: 17,
  },
});

export default HoursByCategoryTable;
